import type {
  BlockNode,
  BoxNode,
  ExperienceDocument,
  GridNode,
  LayoutSnapshot,
  MeasureText,
  Padding,
  ProgressNode,
  ResolvedFont,
  Resolved,
  ScrollNode,
  SizeVal,
  SnapshotNode,
  Span,
  StackNode,
  TextNode,
  Theme,
  Viewport,
} from './ir.js';

// THE NORMATIVE LAYOUT ALGORITHM. Prose definition: packages/schemas/SPEC.md §6.
//
// Single-line flex. No wrap. No shrink. Every divergent behaviour between
// CSS / UIKit / Compose / Flutter is pinned to ONE answer here.
//
//   fill  = equal division of the space remaining after all hug/fixed siblings
//           and gaps, IGNORING the fill child's own content size.
//   hug   = intrinsic content size. NO SHRINK — it overflows rather than clamp.
//   round = distribute floor(), remainder to the LAST. Same law for `fill`,
//           grid columns and progress segments.
//
// layout(tree, constraints) -> rects is a PURE FUNCTION. That is the property everything
// rests on: it is why iOS conformance is an XCTest with no simulator, Android a JVM unit
// test with no emulator, Flutter a `flutter test` with no device. DO NOT ADD AN INPUT TO IT.
//
// Ports of this file (Swift/Kotlin/Dart) must produce identical rects. The ONLY permitted
// source of divergence is measureText, the injected platform primitive.

const ZERO_PAD: Padding = { top: 0, end: 0, bottom: 0, start: 0 };

const KNOWN_BLOCKS = new Set([
  'stack', 'box', 'scroll', 'grid', 'text', 'image', 'video',
  'button', 'spacer', 'custom', 'progress', 'repeat',
]);

/** What the SDK brings to a layout pass. `registry` is the set of identifiers the HOST app has
 *  registered — a set of STRINGS, never a measurement (SPEC §8). */
export interface RenderCtx {
  /** Host-registered `custom` identifiers. */
  registry?: ReadonlySet<string>;
  /** Capabilities this SDK build understands, for `requires[]` (SPEC §11.5). */
  capabilities?: ReadonlySet<string>;
  /** Values for `ref` spans (traits, item.* left over from expand()). */
  bindings?: Readonly<Record<string, string>>;
  /** Items a `repeat` expands over, KEYED BY COLLECTION (SPEC §7.2).
   *
   *  This was a flat array, and `repeat.source.collection` was READ BY NOTHING: every repeat in a
   *  document expanded over the same list, and the field was a key that keyed on nothing. One map,
   *  two sources — `{kind:'campaigns', collection}` reads `items[collection]`, `{kind:'host', key}`
   *  reads `items[key]`. */
  items?: Readonly<Record<string, ReadonlyArray<Readonly<Record<string, string>>>>>;
}

const EMPTY: Required<RenderCtx> = {
  registry: new Set(),
  capabilities: new Set(),
  bindings: {},
  items: {},
};

// ---------------------------------------------------------------------------
// PRE-MEASURE PASSES (SPEC §7). Tree -> tree, run BEFORE measure.
//
// This ordering is LOAD-BEARING. A fallback has its OWN size, so substituting it DURING
// measure would make a node's size depend on a registry lookup mid-pass, and layout() would
// stop being a pure function of (tree, constraints).
// ---------------------------------------------------------------------------

function childrenOf(n: BlockNode): BlockNode[] | null {
  return 'children' in n ? n.children : null;
}

function withChildren(n: BlockNode, children: BlockNode[]): BlockNode {
  return { ...n, children } as BlockNode;
}

/** THE FALLBACK PASS (SPEC §7.1).
 *    unknown block             -> its fallback, else NOTHING
 *    custom, unregistered id   -> its fallback, else NOTHING
 *    requires[] not satisfied  -> its fallback, else NOTHING
 *  "Nothing" means dropped — never a hole, never a throw. */
export function resolve(n: BlockNode, ctx: RenderCtx = EMPTY): BlockNode | null {
  const registry = ctx.registry ?? EMPTY.registry;
  const capabilities = ctx.capabilities ?? EMPTY.capabilities;

  const dead =
    !KNOWN_BLOCKS.has(n.block) ||
    (n.block === 'custom' && !registry.has(n.identifier)) ||
    (n.requires?.some((c) => !capabilities.has(c)) ?? false);

  if (dead) return n.fallback ? resolve(n.fallback, ctx) : null;

  const kids = childrenOf(n);
  if (kids) {
    return withChildren(
      n,
      kids.map((c) => resolve(c, ctx)).filter((c): c is BlockNode => c !== null)
    );
  }
  return n;
}

/** Substitute `item.*` refs in a template copy. Done at EXPANSION time so the measure pass
 *  never has to know which subtree came from which item. */
function bindItem(n: BlockNode, item: Record<string, string>, i: number): BlockNode {
  const sub = (spans: Span[]): Span[] =>
    spans.map((s) => {
      if (s.kind !== 'ref' || !s.path.startsWith('item.')) return s;
      const key = s.path.slice('item.'.length);
      return { kind: 'lit', value: item[key] ?? s.fallback };
    });

  const out = { ...n, id: `${n.id}__${i}` } as BlockNode;
  if (out.block === 'text') out.spans = sub(out.spans);
  if (out.block === 'button') out.label = sub(out.label);

  const kids = childrenOf(out);
  if (kids) return withChildren(out, kids.map((c) => bindItem(c, item, i)));
  return out;
}

/** THE REPEAT PASS (SPEC §7.2). `repeat` becomes a plain column stack of bound template copies.
 *  Not re-entrant: a template may not itself contain a `repeat` (rejected at publish). */
export function expand(n: BlockNode, ctx: RenderCtx = EMPTY): BlockNode {
  if (n.block === 'repeat') {
    // ONE SHAPE, TWO SOURCES. Both arms of `source` are a key into `items` — the SDK's own
    // eligible-campaign list, or a collection the host pushed with `setItems`. A key that resolves
    // to nothing expands to NO CHILDREN: an empty strip, never a crash (SPEC §7.2).
    const key = n.source.kind === 'host' ? n.source.key : n.source.collection;
    const items = ((ctx.items ?? EMPTY.items)[key] ?? []).slice(0, n.max);
    const stack: StackNode = {
      block: 'stack',
      id: n.id,
      direction: 'column',
      gap: 0,
      align: 'stretch',
      justify: 'start',
      size: n.size ?? { width: 'fill', height: 'hug' },
      ...(n.style ? { style: n.style } : {}),
      children: items.map((item, i) => bindItem(n.template, { ...item }, i)),
    };
    return expand(stack, ctx);
  }

  const kids = childrenOf(n);
  if (kids) return withChildren(n, kids.map((c) => expand(c, ctx)));
  return n;
}

// ---------------------------------------------------------------------------
// MEASURE
// ---------------------------------------------------------------------------

export class Ctx {
  readonly bindings: Readonly<Record<string, string>>;

  constructor(
    readonly theme: Theme,
    readonly vp: Viewport,
    readonly measureText: MeasureText,
    bindings: Readonly<Record<string, string>> = {}
  ) {
    this.bindings = bindings;
  }

  /** Token -> concrete value for THIS viewport's colour scheme. Emitted into the snapshot, so
   *  a light/dark snapshot pair is not byte-identical and the scheme actually gates something.
   *  An unknown token returns the token string: the SDK contract is "documented default, never
   *  crash" (SPEC §10). The gatekeeper is what guarantees it never has to. */
  color(token: string): string {
    const c = this.theme.colors[token];
    return c ? c[this.vp.color_scheme] : token;
  }

  font(token: string): ResolvedFont {
    const f = this.theme.fonts[token];
    // Documented default, NOT a throw — SPEC §10. A pinned binary must never crash on a
    // token it has not heard of; publish-time validation is what stops one being sent.
    if (!f) return { family: 'sans-serif', size: 15, weight: 400, lineHeight: 20 };
    // font_scale is clamped by the token's max_scale (SPEC §6.5).
    const scale = Math.min(this.vp.font_scale, f.max_scale);
    const size = f.size * scale;
    return {
      family: f.family,
      size,
      weight: f.weight,
      // Line height is NORMATIVE: platforms must not use their own font metrics (SPEC §6.4).
      lineHeight: Math.round(size * f.line_height),
    };
  }

  /** A `ref` span resolves against the bindings, falling back to its REQUIRED fallback.
   *  A hole in a sentence is not acceptable, so `fallback` is not optional. */
  text(spans: Span[]): string {
    return spans
      .map((s) => (s.kind === 'lit' ? s.value : (this.bindings[s.path] ?? s.fallback)))
      .join('');
  }
}

// ---------------------------------------------------------------------------
// ACCESSIBILITY (SPEC §5.0a). Layout-neutral — nothing here moves a rectangle, so no golden can
// ever gate it. It lives HERE, beside Ctx, because the label resolution is a PURE function of
// (node, bindings) and every renderer must answer it identically. Only each renderer's own suite
// can catch a divergence.
// ---------------------------------------------------------------------------

/** A tappable block is ONE accessibility element (SPEC §5.0a) — it gets the `.button` trait and
 *  its children are hidden from the AT tree. Otherwise VoiceOver reads a card as four unrelated
 *  fragments and the user cannot tell that any of it is tappable. */
export function isA11yElement(n: BlockNode): boolean {
  return !n.a11y_hidden && (n.actions?.length ?? 0) > 0;
}

/** The text a subtree WOULD have read out, in order. Walks THROUGH plain containers — a tappable
 *  card usually wraps its text in a stack, and stopping at the first container would leave the
 *  card silent. */
function subtreeText(n: BlockNode, ctx: Ctx): string[] {
  if (n.a11y_hidden) return [];
  if (n.a11y_label !== undefined) return [n.a11y_label];
  if (n.block === 'text') return [ctx.text(n.spans)];
  if (n.block === 'button') return [ctx.text(n.label)];
  return 'children' in n ? n.children.flatMap((c) => subtreeText(c, ctx)) : [];
}

/** WHAT A SCREEN READER SAYS. The defaults are the whole design:
 *
 *    - an authored `a11y_label` always wins
 *    - `text` / `button` fall back to their own RESOLVED span text — free, and correct
 *    - a TAPPABLE container falls back to the text of its subtree, joined ("Weekend deals, Shop
 *      now"). It is one element, so it needs one name; without this a tappable card is an
 *      unlabelled button, which is the single worst thing to hand a screen-reader user.
 *    - everything else — an `image`, a `video`, a plain `box` — is SILENT. An unlabelled image
 *      announced as "image" is noise, not information. `a11y_hidden` says so explicitly.
 */
export function a11yLabel(n: BlockNode, ctx: Ctx): string | undefined {
  if (n.a11y_hidden) return undefined;
  if (n.a11y_label !== undefined) return n.a11y_label;
  if (n.block === 'text') return ctx.text(n.spans);
  if (n.block === 'button') return ctx.text(n.label);
  if (isA11yElement(n)) {
    const parts = subtreeText(n, ctx).filter((t) => t.length > 0);
    return parts.length > 0 ? parts.join(', ') : undefined;
  }
  return undefined;
}

/** Only the three padded containers have padding; taking them by type keeps the "what if it is
 *  something else" branch from existing at all. */
function padOf(n: StackNode | BoxNode | GridNode): Padding {
  return n.padding ?? ZERO_PAD;
}

function widthSpec(n: BlockNode): SizeVal {
  return n.size?.width ?? 'hug';
}
function heightSpec(n: BlockNode): SizeVal {
  return n.size?.height ?? 'hug';
}

/** THE WIDTH CLAMP (SPEC §6.1b). Applied to a RESOLVED width — the number a node actually got,
 *  after `fill` was divided and `hug` was measured.
 *
 *  IT DOES NOT REDISTRIBUTE. A clamped child's surrendered space is left as a GAP; its siblings do
 *  not grow into it, and `align`/`justify` place the child within it. Handing that space back would
 *  need flexbox's iterative max-resolution loop — five ports, five chances to diverge, for a case
 *  nobody asked for. Every port applies this at the SAME set of call sites; a port that adds one
 *  (or misses one) moves rectangles nobody can explain.
 *
 *  `min_width` may push a child PAST its parent's edge. That is §6.2's no-shrink law holding: we
 *  overflow loudly rather than clamp silently. */
export function clampW(n: BlockNode, w: number): number {
  const s = n.size;
  if (!s) return w;
  let out = w;
  if (s.min_width !== undefined && out < s.min_width) out = s.min_width;
  if (s.max_width !== undefined && out > s.max_width) out = s.max_width;
  return out;
}

/** THE ROUNDING LAW (SPEC §3), shared by grid columns and progress segments:
 *  floor, and the remainder goes to the LAST part. */
export function divide(extent: number, parts: number, spacing: number): number[] {
  const avail = Math.max(0, extent - spacing * Math.max(0, parts - 1));
  const each = Math.floor(avail / parts);
  const out = new Array<number>(parts).fill(each);
  out[parts - 1] = each + (avail - each * parts);
  return out;
}

/** Row-major grouping of grid children into rows of `cols`. */
function gridRows<T>(items: T[], cols: number): T[][] {
  const rows: T[][] = [];
  for (let i = 0; i < items.length; i += cols) rows.push(items.slice(i, i + cols));
  return rows;
}

/** Intrinsic ("hug") width. THE CLAMP LIVES HERE, once — every caller that measures a `hug` node
 *  (and every parent hugging its children) gets it for free, so `max_width`/`min_width` need no
 *  call site of their own on the intrinsic path. */
export function intrinsicWidth(n: BlockNode, ctx: Ctx): number {
  return clampW(n, contentWidth(n, ctx));
}

/** Intrinsic width BEFORE the clamp. A `fill` child inside a hug parent contributes 0 — but a
 *  `min_width` on it is still a floor the parent must hold, which is why the clamp wraps this. */
function contentWidth(n: BlockNode, ctx: Ctx): number {
  switch (n.block) {
    case 'text':
      return ctx.measureText(ctx.text(n.spans), ctx.font(n.font), Infinity).w;
    case 'button': {
      const v = ctx.theme.buttons[n.variant];
      if (!v) return 0;
      const t = ctx.measureText(ctx.text(n.label), ctx.font(n.font), Infinity).w;
      return t + v.padding.start + v.padding.end;
    }
    // `hug` is illegal on all four of these; if one reaches a pinned binary it degrades to 0.
    // For `custom` that is the whole point: it does NOT consult the host view (SPEC §8).
    case 'image':
    case 'video':
    case 'spacer':
    case 'custom':
    case 'progress':
      return typeof widthSpec(n) === 'number' ? (widthSpec(n) as number) : 0;
    case 'stack': {
      const p = padOf(n);
      const kids = n.children.map((c) =>
        typeof widthSpec(c) === 'number' ? (widthSpec(c) as number) : intrinsicWidth(c, ctx)
      );
      const inner =
        n.direction === 'row'
          ? kids.reduce((a, b) => a + b, 0) + n.gap * Math.max(0, kids.length - 1)
          : Math.max(0, ...kids);
      return inner + p.start + p.end;
    }
    case 'box': {
      const p = padOf(n);
      const kids = n.children.map((c) =>
        typeof widthSpec(c) === 'number' ? (widthSpec(c) as number) : intrinsicWidth(c, ctx)
      );
      return Math.max(0, ...kids) + p.start + p.end;
    }
    case 'grid': {
      const p = padOf(n);
      const widest = Math.max(0, ...n.children.map((c) => intrinsicWidth(c, ctx)));
      return widest * n.cols + n.h_spacing * Math.max(0, n.cols - 1) + p.start + p.end;
    }
    case 'scroll':
      // Cross-axis intrinsic; the scroll axis never contributes to intrinsic size.
      return n.axis === 'vertical'
        ? Math.max(0, ...n.children.map((c) => intrinsicWidth(c, ctx)))
        : 0;
    /* c8 ignore next 2 -- expand() removes every `repeat` before measure. */
    case 'repeat':
      return 0;
  }
}

/** THE ONE PLACE a text node's line count is decided (SPEC §6.11) — the intrinsic pass and the
 *  snapshot emitter BOTH go through it. Capping in only one of them would emit a `line_count` that
 *  disagrees with the height it produced: a golden that lies, and `line_count` is an EXACT gate.
 *
 *  The cap is on the COUNT, never the string. `max_lines` at or above the natural count is a
 *  no-op — not a forced height, or a port will pad. */
export function textLines(n: TextNode, w: number, ctx: Ctx): number {
  const natural = ctx.measureText(ctx.text(n.spans), ctx.font(n.font), w).line_count;
  return n.max_lines === undefined ? natural : Math.min(natural, n.max_lines);
}

/** Intrinsic height given a resolved width. */
export function intrinsicHeight(n: BlockNode, w: number, ctx: Ctx): number {
  switch (n.block) {
    case 'text':
      return textLines(n, w, ctx) * ctx.font(n.font).lineHeight;
    case 'button': {
      const v = ctx.theme.buttons[n.variant];
      if (!v) return 0;
      return ctx.font(n.font).lineHeight + v.padding.top + v.padding.bottom;
    }
    // aspect_ratio is REQUIRED, and this is the mechanism it exists for: height-hug derives
    // from the resolved width, so the box is reserved before the bytes land (SPEC §5.6).
    case 'image':
    case 'video':
      return Math.round(w / n.aspect_ratio);
    case 'spacer':
    case 'custom':
    case 'progress':
      return typeof heightSpec(n) === 'number' ? (heightSpec(n) as number) : 0;
    case 'stack':
      return stackHeight(n, w, ctx);
    case 'box': {
      const p = padOf(n);
      const contentW = w - p.start - p.end;
      const kids = n.children.map((c) => childHeight(c, boxChildWidth(c, n, contentW, ctx), ctx));
      return Math.max(0, ...kids) + p.top + p.bottom;
    }
    case 'grid':
      return gridHeight(n, w, ctx);
    case 'scroll':
      // Content height, unbounded. The scroll VIEWPORT's height comes from its own size spec.
      return n.children.reduce((a, c) => a + intrinsicHeight(c, w, ctx), 0);
    /* c8 ignore next 2 -- expand() removes every `repeat` before measure. */
    case 'repeat':
      return 0;
  }
}

/** A child's contribution to a HUG parent's height. An explicit `number` height WINS over the
 *  intrinsic measurement; a `fill` child contributes nothing (no remainder inside a hug parent).
 *  The layout pass gets this right via distributeMain(); the intrinsic pass must agree. */
function childHeight(c: BlockNode, w: number, ctx: Ctx): number {
  const spec = heightSpec(c);
  if (typeof spec === 'number') return spec;
  if (spec === 'fill') return 0;
  return intrinsicHeight(c, w, ctx);
}

function boxChildWidth(c: BlockNode, box: BoxNode, contentW: number, ctx: Ctx): number {
  const spec = widthSpec(c);
  // An explicit size beats `stretch` — see layoutStack.
  if (spec === 'fill') return clampW(c, contentW);
  if (typeof spec === 'number') return spec;
  if (box.align_x === 'stretch') return clampW(c, contentW);
  return intrinsicWidth(c, ctx); // already clamped
}

function gridHeight(n: GridNode, w: number, ctx: Ctx): number {
  const p = padOf(n);
  const cols = divide(w - p.start - p.end, n.cols, n.h_spacing);
  const rows = gridRows(n.children, n.cols);
  const rowHeights = rows.map((row) =>
    // childHeight, NOT intrinsicHeight: an explicit `height` on a cell WINS — that is how a
    // uniform tile grid is built (SPEC §6.8). The COLUMN keeps its width; a clamped cell just
    // occupies less of it (SPEC §6.1b — the clamp never redistributes).
    Math.max(0, ...row.map((c, i) => childHeight(c, clampW(c, cols[i]!), ctx)))
  );
  return (
    rowHeights.reduce((a, b) => a + b, 0) +
    n.v_spacing * Math.max(0, rows.length - 1) +
    p.top +
    p.bottom
  );
}

function stackHeight(n: StackNode, w: number, ctx: Ctx): number {
  const p = padOf(n);
  const contentW = w - p.start - p.end;

  if (n.direction === 'row') {
    // A row child's HEIGHT depends on its MAIN-AXIS width, so main widths must be distributed
    // BEFORE heights are measured (SPEC §6.3). Getting this wrong made a 56x56 aspect-ratio:1
    // icon resolve to 294x294.
    const specs = n.children.map((c) => widthSpec(c));
    const intrinsics = n.children.map((c) => intrinsicWidth(c, ctx));
    // Clamp AFTER the division, and leave the gap (SPEC §6.1b). The intrinsic pass must agree with
    // the layout pass or the same node measures one way and lays out another — §6.8b's old bug.
    const widths = distributeMain(specs, intrinsics, contentW, n.gap).map((w, i) =>
      clampW(n.children[i]!, w)
    );
    const kids = n.children.map((c, i) => childHeight(c, widths[i]!, ctx));
    return Math.max(0, ...kids) + p.top + p.bottom;
  }

  const kids = n.children.map((c) => childHeight(c, resolveCross(c, n, contentW, ctx), ctx));
  return kids.reduce((a, b) => a + b, 0) + n.gap * Math.max(0, kids.length - 1) + p.top + p.bottom;
}

/** Cross-axis size of a child (width when the parent is a column). */
function resolveCross(child: BlockNode, parent: StackNode, contentCross: number, ctx: Ctx): number {
  if (parent.direction !== 'column') return contentCross;
  const spec = widthSpec(child);
  if (spec === 'fill') return clampW(child, contentCross);
  if (typeof spec === 'number') return spec;
  // hug: NO SHRINK — intrinsic even if it overflows. Overflow is a conformance failure, not a
  // silent clamp (SPEC §6.2).
  if (parent.align === 'stretch') return clampW(child, contentCross);
  return intrinsicWidth(child, ctx); // already clamped
}

/** THE fill RULE (SPEC §6.1). Divides `avail` among the fill children, IGNORING their content. */
export function distributeMain(
  specs: SizeVal[],
  intrinsics: number[],
  avail: number,
  gap: number
): number[] {
  const n = specs.length;
  const out: (number | null)[] = specs.map((s, i) => {
    if (s === 'fill') return null;
    if (typeof s === 'number') return s;
    return intrinsics[i]!;
  });
  const fillIdx = specs.map((s, i) => (s === 'fill' ? i : -1)).filter((i) => i >= 0);
  const used = out.reduce<number>((a, b) => a + (b ?? 0), 0) + gap * Math.max(0, n - 1);
  const remaining = Math.max(0, avail - used);

  if (fillIdx.length > 0) {
    const each = Math.floor(remaining / fillIdx.length);
    for (const i of fillIdx) out[i] = each;
    // Rounding is NORMATIVE: the remainder goes to the LAST fill child.
    const last = fillIdx[fillIdx.length - 1]!;
    out[last] = (out[last] as number) + (remaining - each * fillIdx.length);
  }
  return out as number[];
}

function alignOffset(mode: string, free: number): number {
  if (mode === 'center') return Math.floor(free / 2);
  if (mode === 'end') return free;
  return 0;
}

// ---------------------------------------------------------------------------
// LAYOUT
// ---------------------------------------------------------------------------

/** Token-resolved style. Every colour here is a token, so all of them are gated exactly —
 *  none of them moves a rectangle, so frames could never catch them. */
function styleOf(n: BlockNode, ctx: Ctx): Resolved | undefined {
  const s = n.style;
  const tint = n.block === 'image' ? n.tint : undefined;
  if (!s && tint === undefined) return undefined;
  const r: Resolved = {};
  // A GRADIENT'S STOPS ARE TOKENS TOO (SPEC §5.0c), so they resolve through here or the light/dark
  // pair is byte-identical and the colour scheme gates nothing on the biggest surface in the card.
  if (typeof s?.bg === 'string') r.bg = ctx.color(s.bg);
  else if (s?.bg) r.bg_stops = s.bg.stops.map((t) => ctx.color(t));
  if (s?.border) r.border_color = ctx.color(s.border.color);
  if (s?.shadow) r.shadow_color = ctx.color(s.shadow.color);
  if (tint) r.tint = ctx.color(tint);
  return Object.keys(r).length > 0 ? r : undefined;
}

function push(out: SnapshotNode[], n: BlockNode, frame: SnapshotNode['frame'], ctx: Ctx): void {
  const node: SnapshotNode = { id: n.id, frame };
  const r = styleOf(n, ctx);
  if (r) node.resolved = r;
  out.push(node);
}

export function layoutStack(
  n: StackNode, x: number, y: number, w: number, h: number, ctx: Ctx, out: SnapshotNode[]
): void {
  push(out, n, [x, y, w, h], ctx);

  const p = padOf(n);
  const contentX = x + p.start;
  const contentY = y + p.top;
  const contentW = w - p.start - p.end;
  const contentH = h - p.top - p.bottom;

  const isRow = n.direction === 'row';
  const mainAvail = isRow ? contentW : contentH;
  const crossAvail = isRow ? contentH : contentW;

  // 1. Cross sizes first for columns (width drives text wrapping, which drives height).
  const crossSizes = n.children.map((c) => {
    const spec = isRow ? heightSpec(c) : widthSpec(c);
  // AN EXPLICIT SIZE BEATS `stretch`. This used to read `spec === 'fill' || align === 'stretch'`,
  // so a child that asked for 60pt in a stretch container got the container's width instead — the
  // author was simply ignored. It also disagreed with the INTRINSIC path a few lines up, which has
  // always checked the number first, so the same node measured one way and laid out another.
  // Flexbox has the same law (align-items: stretch applies only when the cross size is auto), and
  // the CSS-flow arm implements it — which is how this surfaced: a 60pt dots row came out 390pt.
    // The cross axis is a WIDTH only in a column — clampW is a width law and must not touch a
    // row's cross-axis HEIGHT (SPEC §6.1b: there is no max_height, deliberately).
    const clamp = (v: number) => (isRow ? v : clampW(c, v));
    if (spec === 'fill') return clamp(crossAvail);
    if (typeof spec === 'number') return spec;
    if (n.align === 'stretch') return clamp(crossAvail);
    return isRow ? 0 : intrinsicWidth(c, ctx); // row hug-height resolved in step 3
  });

  // 2. Main sizes. In a ROW the main axis is the width, so the clamp lands here instead — after
  // the division, and without redistributing the slack (SPEC §6.1b).
  const mainSpecs = n.children.map((c) => (isRow ? widthSpec(c) : heightSpec(c)));
  const mainIntrinsics = n.children.map((c, i) =>
    isRow ? intrinsicWidth(c, ctx) : intrinsicHeight(c, crossSizes[i]!, ctx)
  );
  const mainSizes = distributeMain(mainSpecs, mainIntrinsics, mainAvail, n.gap).map((v, i) =>
    isRow ? clampW(n.children[i]!, v) : v
  );

  // 3. Row hug-height needs the resolved width (SPEC §6.3).
  if (isRow) {
    n.children.forEach((c, i) => {
      if (heightSpec(c) === 'hug' && n.align !== 'stretch') {
        crossSizes[i] = intrinsicHeight(c, mainSizes[i]!, ctx);
      }
    });
  }

  // 4. Position.
  const totalMain =
    mainSizes.reduce((a, b) => a + b, 0) + n.gap * Math.max(0, n.children.length - 1);
  const freeMain = Math.max(0, mainAvail - totalMain);
  let cursor = n.justify === 'space_between' ? 0 : alignOffset(n.justify, freeMain);
  const between =
    n.justify === 'space_between' && n.children.length > 1
      ? Math.floor(freeMain / (n.children.length - 1))
      : 0;

  n.children.forEach((c, i) => {
    const ms = mainSizes[i]!;
    const cs = crossSizes[i]!;
    const co = n.align === 'stretch' ? 0 : alignOffset(n.align, Math.max(0, crossAvail - cs));

    const cx = isRow ? contentX + cursor : contentX + co;
    const cy = isRow ? contentY + co : contentY + cursor;
    layoutNode(c, cx, cy, isRow ? ms : cs, isRow ? cs : ms, ctx, out);
    cursor += ms + n.gap + between;
  });
}

/** Z-stack: children share the content rect, placed by ALIGNMENT. No coordinates, ever. */
export function layoutBox(
  n: BoxNode, x: number, y: number, w: number, h: number, ctx: Ctx, out: SnapshotNode[]
): void {
  push(out, n, [x, y, w, h], ctx);

  const p = padOf(n);
  const cx = x + p.start;
  const cy = y + p.top;
  const cw = w - p.start - p.end;
  const ch = h - p.top - p.bottom;

  for (const c of n.children) {
    const childW = boxChildWidth(c, n, cw, ctx);
    const hSpec = heightSpec(c);
    // An explicit size beats `stretch` — see layoutStack.
    const childH =
      hSpec === 'fill'
        ? ch
        : typeof hSpec === 'number'
          ? hSpec
          : n.align_y === 'stretch'
            ? ch
            : intrinsicHeight(c, childW, ctx);

    const ox = n.align_x === 'stretch' ? 0 : alignOffset(n.align_x, Math.max(0, cw - childW));
    const oy = n.align_y === 'stretch' ? 0 : alignOffset(n.align_y, Math.max(0, ch - childH));
    layoutNode(c, cx + ox, cy + oy, childW, childH, ctx, out);
  }
}

export function layoutGrid(
  n: GridNode, x: number, y: number, w: number, h: number, ctx: Ctx, out: SnapshotNode[]
): void {
  push(out, n, [x, y, w, h], ctx);

  const p = padOf(n);
  const cx = x + p.start;
  const colW = divide(w - p.start - p.end, n.cols, n.h_spacing);
  const rows = gridRows(n.children, n.cols);

  let rowY = y + p.top;
  for (const row of rows) {
    const widths = row.map((c, i) => clampW(c, colW[i]!));
    const heights = row.map((c, i) => childHeight(c, widths[i]!, ctx));
    const rowH = Math.max(0, ...heights);
    let colX = cx;
    row.forEach((c, i) => {
      // Its OWN height, not the row height. Forcing the row height onto a cell fights
      // aspect-ratio and pushes the width back out past the column.
      //
      // A clamped cell sits at the column's START and the column keeps its own width — the next
      // column does not slide left into the slack (SPEC §6.1b).
      layoutNode(c, colX, rowY, widths[i]!, heights[i]!, ctx, out);
      colX += colW[i]! + n.h_spacing;
    });
    rowY += rowH + n.v_spacing;
  }
}

/** Scroll: content is laid out UNBOUNDED along `axis`. Overflow there is expected (SPEC §6.7). */
export function layoutScroll(
  n: ScrollNode, x: number, y: number, w: number, h: number, ctx: Ctx, out: SnapshotNode[]
): void {
  const node: SnapshotNode = { id: n.id, frame: [x, y, w, h], scrolls: n.axis };
  const r = styleOf(n, ctx);
  if (r) node.resolved = r;
  out.push(node);

  const vertical = n.axis === 'vertical';
  const viewport = vertical ? h : w;
  let cursor = vertical ? y : x;
  for (const c of n.children) {
    // ALONG the axis an explicit size WINS, else intrinsic. Using intrinsic unconditionally
    // silently discarded a card's `width: 110` and sized it to its text.
    //
    // `fill` ALONG THE AXIS IS ONE PAGE — the scroll's own extent. Everywhere else `fill` means
    // "divide the parent's remaining space", and an unbounded axis has no remainder to divide, so
    // this used to be rejected at publish. But that left a full-bleed carousel INEXPRESSIBLE: a
    // card cannot be authored "as wide as the slot" when the slot is 358pt on one phone and 430 on
    // another, and hard-coding a width breaks on every device but one.
    //
    // Reading it as a page keeps the meaning of `fill` intact — take the parent's available extent
    // — because along a scroll axis the extent available to ONE item is exactly the viewport. It is
    // also what a browser does with `width: 100%` inside a scroll container. `hug` along the axis
    // would make the page width circular, so the gatekeeper rejects that pair.
    const mainSpec = vertical ? heightSpec(c) : widthSpec(c);
    const main =
      typeof mainSpec === 'number'
        ? mainSpec
        : mainSpec === 'fill'
          ? viewport
          : vertical
            ? intrinsicHeight(c, w, ctx)
            : intrinsicWidth(c, ctx);
    // ACROSS the axis the child fills the viewport extent. Both axes route the WIDTH through the
    // clamp: a card in a horizontal rail is clamped along the axis, a row in a vertical scroll
    // across it (SPEC §6.1b).
    const cw = clampW(c, vertical ? w : main);
    const ch = vertical ? main : h;
    layoutNode(c, vertical ? x : cursor, vertical ? cursor : y, cw, ch, ctx, out);
    cursor += main;
  }
}

/** Segmented bar. Segment widths use divide() — THE SAME floor/remainder-to-the-LAST law as
 *  `fill` and grid columns. Five segments in 294pt with a 4pt gap are 55/55/55/55/58. */
export function layoutProgress(
  n: ProgressNode, x: number, y: number, w: number, h: number, ctx: Ctx, out: SnapshotNode[]
): void {
  push(out, n, [x, y, w, h], ctx);

  const segs = divide(w, n.segments, n.gap);
  let cx = x;
  segs.forEach((sw, i) => {
    // TWO SEMANTICS (SPEC §6.10). A progress BAR lights a PREFIX — "step 2 of 3" means the first two
    // are behind you. A PAGE INDICATOR (`tracks` a scroll) lights exactly ONE: the card you are
    // looking at. A prefix would claim you had "completed 2 of 3 cards", which no carousel means.
    const lit = n.tracks === undefined ? i < n.value : i === n.value - 1;
    out.push({
      id: `${n.id}_seg${i}`,
      frame: [cx, y, sw, h],
      // A lit segment must resolve to a different token than an unlit one. An off-by-one or a
      // light/dark bug here moves NO rectangle — frames alone can never see it. This is the whole
      // reason `resolved` is part of the conformance bar.
      resolved: { bg: ctx.color(lit ? n.fill_color : n.track_color) },
    });
    cx += sw + n.gap;
  });
}

export function layoutNode(
  n: BlockNode, x: number, y: number, w: number, h: number, ctx: Ctx, out: SnapshotNode[]
): void {
  switch (n.block) {
    case 'stack':
      return layoutStack(n, x, y, w, h, ctx, out);
    case 'box':
      return layoutBox(n, x, y, w, h, ctx, out);
    case 'grid':
      return layoutGrid(n, x, y, w, h, ctx, out);
    case 'scroll':
      return layoutScroll(n, x, y, w, h, ctx, out);
    case 'progress':
      return layoutProgress(n, x, y, w, h, ctx, out);
    /* c8 ignore next 2 -- expand() removes every `repeat` before measure. */
    case 'repeat':
      return;
    default: {
      const node: SnapshotNode = { id: n.id, frame: [x, y, w, h] };
      const style = styleOf(n, ctx);

      if (n.block === 'text') {
        const f = ctx.font(n.font);
        node.line_count = textLines(n, w, ctx);
        node.resolved = {
          ...style,
          color: ctx.color(n.color),
          font_size: f.size,
          line_height: f.lineHeight,
        };
      } else if (n.block === 'button') {
        const v = ctx.theme.buttons[n.variant];
        const f = ctx.font(n.font);
        node.resolved = {
          ...style,
          ...(v
            ? {
                color: ctx.color(v.fg),
                ...(v.bg ? { bg: ctx.color(v.bg) } : {}),
              }
            : {}),
          font_size: f.size,
          line_height: f.lineHeight,
        };
      } else if (style) {
        node.resolved = style;
      }
      out.push(node);
    }
  }
}

/** THE ROOT'S HEIGHT (SPEC §6.1c).
 *
 *  `fill` MEANS THE VIEWPORT, and this is the §6.10 argument again, verbatim. Everywhere else `fill`
 *  divides the parent's *remaining* space — and the root has no parent, so by the arithmetic there is
 *  no remainder and `fill` should be 0. That was true of the arithmetic and false of the product: it
 *  left a bottom-pinned button INEXPRESSIBLE. A fullscreen onboarding cannot put "Next" on the bottom
 *  edge, because a `fill` spacer above it collapses to 0 inside a hugging root (§6.2), and a hugging
 *  root is the only kind there was. The extent available to the root simply IS the viewport — which
 *  is also what a browser does with `height: 100%` on a body.
 *
 *  It is capped to `fullscreen` AT PUBLISH, not here (`checkRootFill`): the chromes anchor every
 *  surface off the root's own solved height, so a viewport-tall root makes a bottom sheet's
 *  `originY = screenHeight - documentHeight` equal ZERO — the sheet would render at the top of the
 *  screen with its detents collapsed. The solver stays a pure function of (document, viewport) and
 *  does not know about surfaces; the gatekeeper is what stops the document reaching it.
 *
 *  A NUMBER is honoured too. It was silently dropped before — an explicit 700 laid out as a hug —
 *  which is the same defect wearing a different hat.
 *
 *  `hug` is UNCHANGED, and that is what keeps every existing golden byte-identical: every fixture in
 *  the corpus roots at `height: "hug"`. */
function rootHeight(root: BlockNode, w: number, ctx: Ctx, vp: Viewport): number {
  const spec = heightSpec(root);
  if (spec === 'fill') return vp.h;
  if (typeof spec === 'number') return spec;
  return intrinsicHeight(root, w, ctx);
}

/** THE ENTRY POINT. Pre-measure passes first, then measure a tree of only known, live blocks. */
export function layout(
  doc: ExperienceDocument,
  vp: Viewport,
  measureText: MeasureText,
  render: RenderCtx = EMPTY
): LayoutSnapshot {
  const ctx = new Ctx(doc.theme, vp, measureText, render.bindings ?? EMPTY.bindings);

  // Order matters: expand() first (a repeat's template may carry fallbacks), then resolve().
  const expanded = expand(doc.root, render);
  const root = resolve(expanded, render);

  const nodes: SnapshotNode[] = [];
  if (root) {
    // The ROOT is where max_width earns its keep: `fill` on an iPad is a 1024pt card, and the root
    // is already centred in the viewport, so the clamp lands the card in the middle of the screen
    // instead of stretching it across one (SPEC §6.1b).
    const w = clampW(root, typeof widthSpec(root) === 'number' ? (widthSpec(root) as number) : vp.w);
    const h = rootHeight(root, w, ctx, vp);
    // Root origin is fixed so every renderer compares in the same coordinate space.
    layoutNode(root, Math.floor((vp.w - w) / 2), 0, w, h, ctx, nodes);
  }

  // RTL is a MIRROR OF OUR OWN OUTPUT, applied after layout. Because we own the solver, every
  // renderer that positions from our rects agrees by construction. (renderer-web is the
  // exception — it delegates to CSS — see SPEC §6.9.)
  if (vp.direction === 'rtl') {
    for (const n of nodes) n.frame[0] = vp.w - n.frame[0] - n.frame[2];
  }

  return { viewport: vp, nodes };
}
